import { useEffect } from 'react';

/**
 * UpdateBanner
 * ─────────────────────────────────────────────────────────────────────────
 * Owns the Service Worker registration + update *detection* for the app.
 *
 * Why this exists:
 *   Installed PWAs (iOS especially) can stay pinned on an old bundle for
 *   days after a deploy. This component:
 *     1. Registers /sw.js
 *     2. Periodically asks the browser to check for a new SW version
 *     3. Auto-recovers from chunk-load errors after a deploy
 *
 *   It deliberately does NOT force-activate a waiting SW or reload the page
 *   on its own anymore (removed while debugging the Android login-button
 *   issue — see the chunk-error handler below for why that's dangerous).
 *   A waiting SW just activates itself the next time every tab/instance of
 *   the app is fully closed, per the browser's normal SW lifecycle — no
 *   code needed for that part.
 */
export default function UpdateBanner() {
  // Register SW + keep asking the browser to check for updates. We never
  // act on the result (see the file-level comment) — this just keeps a
  // fresh SW file downloaded and "waiting" in the background so it's ready
  // to take over next time the app fully restarts.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const setup = async () => {
      try {
        const reg = await navigator.serviceWorker.register('/sw.js');

        // Force the browser to check for an SW update every time the page
        // becomes visible again. This guarantees iOS PWAs have a fresh
        // bundle downloaded and ready without needing a manual restart.
        const onVisible = () => {
          if (document.visibilityState === 'visible') {
            reg.update().catch(() => null);
          }
        };
        document.addEventListener('visibilitychange', onVisible);

        // Periodic update check (every 30 min while the app is open).
        const id = setInterval(() => reg.update().catch(() => null), 30 * 60 * 1000);

        return () => {
          document.removeEventListener('visibilitychange', onVisible);
          clearInterval(id);
        };
      } catch (e) {
        // SW unsupported / blocked — non-fatal.
      }
    };

    let cleanup;
    setup().then((c) => { cleanup = c; });
    return () => cleanup?.();
  }, []);

  // Recovery hook: if the page loaded a hashed JS chunk that no longer
  // exists (because the deploy moved on), force a hard reload. This is
  // the silent fallback when the user never clicks the banner.
  //
  // Iter Feb 2026 — we also catch the "Unexpected token '<'" symptom
  // that appears when the SW (or a caching proxy) serves the SPA
  // index.html under a JS URL. The parse fires as a SyntaxError, not a
  // ChunkLoadError, so the earlier detector missed it and the app got
  // stuck on the red overlay until a manual hard reload.
  useEffect(() => {
    const onChunkError = (e) => {
      const rawMsg = String(e?.message || e?.reason?.message || e?.reason || '');
      const looksLikeChunkError =
        rawMsg.includes('Loading chunk') ||
        rawMsg.includes('ChunkLoadError') ||
        rawMsg.includes('Failed to fetch dynamically imported module') ||
        // HTML served as JS ⇒ browser throws while parsing "<!doctype…"
        /Unexpected token\s*['"]?<['"]?/.test(rawMsg) ||
        // Some engines phrase it differently
        rawMsg.includes("Unexpected token '<'");
      if (looksLikeChunkError) {
        // Avoid a reload loop using sessionStorage.
        const key = 'tofbox_chunk_reload_at';
        const last = parseInt(sessionStorage.getItem(key) || '0', 10);
        if (Date.now() - last > 30000) {
          sessionStorage.setItem(key, String(Date.now()));
          // Purge the poisoned SW caches BEFORE reloading so the new
          // page load doesn't hit the same bad entries again.
          (async () => {
            try {
              if ('caches' in window) {
                const keys = await caches.keys();
                await Promise.all(keys.map((k) => caches.delete(k).catch(() => null)));
              }
            } catch { /* ignore */ }
            // Cache-bust the URL so the fresh HTML doesn't come back stale.
            const url = new URL(window.location.href);
            url.searchParams.set('_v', Date.now().toString());
            window.location.replace(url.toString());
          })();
        }
      }
    };
    window.addEventListener('error', onChunkError);
    window.addEventListener('unhandledrejection', onChunkError);
    return () => {
      window.removeEventListener('error', onChunkError);
      window.removeEventListener('unhandledrejection', onChunkError);
    };
  }, []);

  // No visible banner. Keeping the component mounted so the SW registration
  // above and the chunk-error auto-recovery below continue to run. Manual
  // cache purge (e.g. the "Vider le cache et recharger" link on the login
  // page) goes through the standalone forcePwaPurge() export below instead.
  return null;
}

/**
 * Imperative purge — usable from anywhere (Settings page, error screen, etc.)
 */
export async function forcePwaPurge() {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => null)));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => null)));
    }
  } catch (_) { /* swallow */ }
  const url = new URL(window.location.href);
  url.searchParams.set('_v', Date.now().toString());
  window.location.replace(url.toString());
}
